/**
 * Studio Types (Creator Dashboard)
 * Types for creator studio and content management
 */

export interface StudioCreator {
  id: number;
  name: string;
  channel_slug: string;
  avatar_url: string;
}

export interface StudioStats {
  totalVideos: number;
  totalShorts: number;
  totalPosts: number;
  totalViews?: number;
  totalWatchTimeSeconds?: number;
}

export interface StudioContentItem {
  id: number;
  uuid: string;
  title: string;
  thumbnail_url: string;
  created_at: string;
  views_count?: number;
  likes_count?: number;
}

export interface StudioDashboardResponse {
  creator: StudioCreator;
  stats: StudioStats;
  latestVideos: StudioContentItem[];
  latestShorts: StudioContentItem[];
}

/**
 * Bunny CDN processing status codes known to the frontend
 *
 * Typical lifecycle:
 * 0 (Queued) → 6 (UploadStarted) → 7 (UploadFinished) → 1 (Processing)
 *   → 2 (Encoding) → 4 (ResolutionFinished) → 3 (Finished)
 *
 * 0  = Queued - Video waiting to be processed
 * 1  = Processing - Preview/format analysis in progress
 * 2  = Encoding - Actively encoding resolutions
 * 3  = Finished - All resolutions complete, fully encoded
 * 4  = ResolutionFinished - At least one resolution done, video is playable
 * 5  = Failed - Encoding failed
 * 6  = PresignedUploadStarted - TUS presigned upload started
 * 7  = PresignedUploadFinished - TUS presigned upload completed
 * 8  = PresignedUploadFailed - TUS presigned upload failed
 * 9  = CaptionsGenerated - Auto-captions generated
 * 10 = TitleOrDescriptionGenerated - AI-generated title/description
 *
 * 11–13 = post-ready states observed in prod (2026-08-18), playable.
 *
 * Canonical contract: tabootv_bo/documentation/api-bunny-status.md. Readiness
 * is a BLOCKLIST (BUNNY_NOT_READY_STATUSES) — unknown future values default to
 * playable, so a new post-ready state never hides videos from pickers again.
 */
export type BunnyStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;

/** Statuses where video is still being processed (not yet playable) */
export const BUNNY_PROCESSING_STATUSES: ReadonlySet<number> = new Set([0, 1, 2]);

/** Statuses where video is uploading via TUS */
export const BUNNY_UPLOADING_STATUSES: ReadonlySet<number> = new Set([6, 7]);

/** Statuses indicating a failure */
export const BUNNY_FAILED_STATUSES: ReadonlySet<number> = new Set([5, 8]);

/**
 * Statuses where a Bunny video is definitively NOT playable. The complement —
 * not membership in BUNNY_READY_STATUSES — defines playability, so unknown
 * future statuses default to playable instead of silently disappearing from
 * pickers (root cause of the bunny_status=13 pin-picker bug).
 */
export const BUNNY_NOT_READY_STATUSES: ReadonlySet<number> = new Set([0, 1, 2, 5, 6, 7, 8]);

/**
 * Whether a studio video is playable/addable (pin pickers, course attach).
 * Legacy non-Bunny videos qualify when published; Bunny videos qualify unless
 * their status is a known not-ready state. Missing bunny_status defaults to
 * playable, mirroring deriveProcessingStatus's 'ready' fallback.
 */
export function isStudioVideoPlayable(
  video: Pick<StudioVideoListItem, 'is_bunny_video' | 'published' | 'bunny_status'>
): boolean {
  if (video.is_bunny_video === false) return video.published === true;
  if (video.bunny_status === undefined) return true;
  return !BUNNY_NOT_READY_STATUSES.has(video.bunny_status);
}

/**
 * Publish schedule for scheduled videos
 */
export interface PublishSchedule {
  scheduled_at: string;
  publish_mode?: PublicationMode;
  notify?: boolean;
}

/**
 * Publication mode as used by the API
 * - 'none': Draft - not published, no schedule
 * - 'auto': Publish Now — schedules 1 minute from now
 * - 'scheduled': Scheduled - will publish at scheduled_at time
 */
export type PublicationMode = 'none' | 'auto' | 'scheduled';

/**
 * Video display state for UI rendering
 * Derived from API fields (published, publish_schedule, bunny_status)
 */
export type VideoDisplayState = 'live' | 'draft' | 'scheduled' | 'processing' | 'failed';

export interface StudioVideoListItem {
  id: number;
  uuid: string;
  /** Nullable since BE 289070c — shorts may be uploaded with no title at all. */
  title: string | null;
  description?: string;
  thumbnail?: string;
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
  duration?: number;
  created_at: string;
  published_at?: string;
  status?: string;
  /** Whether the video is published (live) */
  published?: boolean;
  /** Video is currently being processed by Bunny */
  processing?: boolean;
  /** Bunny processing status — known values in BunnyStatus, but BE emits beyond that range (e.g. 13) */
  bunny_status?: number;
  /** Processing progress percentage (0-100) */
  progress?: number;
  /** Bunny encoding progress percentage */
  bunny_encode_progress?: number;
  /**
   * Bunny encoder failure detail (PR A — bunny_last_error). Present on
   * `bunny_status=5` rows when BE owner-gate allows. Replaces the legacy
   * generic copy on FAILED rows. See `formatBunnyFailureCopy`.
   */
  bunny_last_error?: string | null;
  /** ISO 8601 timestamp from Bunny encoder clock when the error was logged. */
  bunny_last_error_at?: string | null;
  /**
   * Structured Bunny encoding log array (PR B — bunny_encoding_log).
   * Optional; populated on `bunny_status=5` rows once BE backfill runs.
   * Consumed by the timeline UI in a follow-up ticket.
   */
  bunny_encoding_log?: import('./video').BunnyLogEntry[] | null;
  /** Available video resolutions (comma-separated, e.g., "720p,1080p") */
  bunny_available_resolutions?: string;
  /** Whether the video is hosted on Bunny CDN */
  is_bunny_video?: boolean;
  /** Whether this is a short video */
  short?: boolean;
  /** Scheduled publication info */
  publish_schedule?: PublishSchedule | null;
  /** Whether video is hidden from public listings */
  hidden?: boolean;
  /** Location name */
  location?: string;
  /** Country name */
  country?: string;
  /** Country ID */
  country_id?: number;
  /** Latitude coordinate */
  latitude?: number;
  /** Longitude coordinate */
  longitude?: number;
  /** Tags associated with the video */
  tags?: string[];
  /** Whether the video contains adult/sensitive content */
  is_adult_content?: boolean;
  /** Whether the video contains substance-related content */
  is_substance_content?: boolean;
  /** Whether the video is exclusive content */
  is_exclusive_content?: boolean;
  /** Whether a 4K rendition exists — backend transcode output, never creator-set. */
  has_4k?: boolean;
  /** Whether the video is publicly accessible without auth/subscription */
  is_public?: boolean;
  /**
   * Parent series/course numeric id. Null when the video has no parent.
   * Already emitted by BE on every StudioVideoList row — used to pre-filter
   * the course attach picker and seed the upload-modal Playlists picker in
   * edit mode.
   */
  series_id?: number | null;
  /** Parent series/course uuid. Null when the video has no parent. */
  series_uuid?: string | null;
  /** Parent series/course title. Null when the video has no parent. */
  series_title?: string | null;
  /** Parent series/course type discriminator. Null when the video has no parent. */
  series_type?: 'series' | 'course' | null;
}

/**
 * GET /studio/videos/{uuid} — the base Video plus the publish-state fields
 * the list endpoint also emits (confirmed on the wire 2026-08-20, BACK-185).
 */
export type StudioVideoDetail = import('./video').Video &
  Pick<StudioVideoListItem, 'published' | 'publish_schedule'>;
export type StudioShortListItem = StudioVideoListItem;
export type StudioShortDetail = StudioVideoListItem;

export interface StudioPostListItem {
  id: number;
  uuid: string;
  body: string;
  created_at: string;
  published_at?: string;
  likes_count?: number;
  comments_count?: number;
  post_image?: string[];
  location?: string;
  latitude?: string;
  longitude?: string;
}

export type StudioPostDetail = StudioPostListItem;

export interface StudioSeriesListItem {
  id: number;
  uuid: string;
  title: string;
  description?: string;
  thumbnail_url?: string;
  videos_count?: number;
  created_at: string;
  published_at?: string;
}

export type StudioSeriesDetail = StudioSeriesListItem;
export type StudioCourseListItem = StudioSeriesListItem;
export type StudioCourseDetail = StudioSeriesListItem;

export interface StudioVideosListResponse {
  videos: StudioVideoListItem[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface StudioPostsListResponse {
  posts: StudioPostListItem[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

/**
 * Prepare Bunny Upload Types
 * For direct TUS uploads bypassing server action body limit
 *
 * Two modes (BE: documentation/api-prepare-bunny-upload-resume.md):
 *   - Mint mode (no `video_uuid`): creates new Video row + Bunny VideoId.
 *   - Refresh mode (`video_uuid` set): returns a fresh TUS signature for an
 *     existing `bunny_video_id`. Required for cross-reload TUS resume —
 *     tus-js-client's persisted upload URL is bound to a specific VideoId.
 */
export interface PrepareBunnyUploadPayload {
  /** When set, BE refreshes the signature for the existing video instead of minting a new record. */
  video_uuid?: string | undefined;
  /**
   * Optional since BE 289070c. Omitted for bulk shorts so the creator names
   * them, not their filesystem; BE substitutes its own Bunny dashboard label.
   */
  title?: string | undefined;
  description?: string | undefined;
  short?: boolean | undefined;
  duration?: number | undefined;
  series_id?: number | undefined;
  location?: string | undefined;
  country_id?: number | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  is_adult_content?: boolean | undefined;
  is_substance_content?: boolean | undefined;
  is_exclusive_content?: boolean | undefined;
  is_public?: boolean | undefined;
  tags?: number[] | undefined;
  thumbnail_path?: string | undefined;
  publish_mode?: 'none' | 'auto' | 'scheduled' | undefined;
  scheduled_at?: string | undefined;
}

export interface PrepareBunnyUploadResponse {
  message: string;
  video_id: number;
  video_uuid: string;
  bunny_video_id: string;
  upload_config: {
    endpoint: string;
    headers: {
      AuthorizationSignature: string;
      AuthorizationExpire: number;
      LibraryId: string | number;
      VideoId: string;
    };
  };
}

/**
 * On-demand Bunny status sync (BE: BACK-209, `documentation/api-bunny-status.md`).
 *
 * `bunny_status` is a BE column fed by a Bunny webhook, not a live proxy. When
 * that webhook stream lags — it ran 38 minutes behind Bunny's own live API on
 * 2026-09-08 — this endpoint asks Bunny directly and syncs the column.
 *
 * Owner-only, rate-limited 6/min **per video**. Response is flat (fields at top
 * level alongside `message`), same convention as `prepareBunnyUpload`.
 */
export interface RefreshBunnyStatusResponse {
  message: string;
  video_uuid: string;
  bunny_status: number | null;
  bunny_status_updated_at: string | null;
  bunny_status_source: BunnyStatusSource | null;
  /** What Bunny reported live, before any sync decision. */
  live_bunny_status: number;
  /** Whether this call actually advanced the stored column. */
  synced_now: boolean;
}

/** Which writer last set `bunny_status`. Absent on non-Bunny videos, and null
 * on rows written before BACK-209 shipped (the migration has no backfill). */
export type BunnyStatusSource = 'webhook' | 'reconciler' | 'initial_upload' | 'manual_refresh';

/** Error codes the refresh endpoint returns in its error payload. */
export type RefreshBunnyStatusErrorCode =
  'NOT_BUNNY_VIDEO' | 'RATE_LIMITED' | 'BUNNY_UNREACHABLE' | 'BUNNY_404';

/**
 * Fetch-from-URL Upload Types
 * For the server-side ingestion path (BE: documentation/api-studio-video-fetch.md):
 * the creator hands a direct video URL to the backend, Bunny downloads it
 * server-to-server, and the creator uploads zero bytes. Sibling to the TUS
 * `PrepareBunnyUploadPayload` flow — same metadata contract, different endpoint
 * (`POST /studio/videos/fetch-from-url`).
 */
export interface FetchFromUrlPayload {
  /** Fully-qualified http(s) URL that resolves to the video bytes. */
  source_url: string;
  title: string;
  /** Headers Bunny attaches to its outbound GET (e.g. Authorization for private S3). */
  source_headers?: Record<string, string> | undefined;
  /**
   * Idempotency key. One value per Import intent, reused across retries: a
   * repeat with the same key short-circuits BE-side to the existing row
   * instead of dispatching a second Bunny fetch.
   */
  client_id?: string | undefined;
  description?: string | undefined;
  short?: boolean | undefined;
  duration?: number | undefined;
  series_id?: number | undefined;
  location?: string | undefined;
  country_id?: number | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  /** BE accepts both is_sensitive_content and the legacy is_adult_content alias. */
  is_sensitive_content?: boolean | undefined;
  is_adult_content?: boolean | undefined;
  is_substance_content?: boolean | undefined;
  is_exclusive_content?: boolean | undefined;
  is_public?: boolean | undefined;
  language?: string | undefined;
  /** Tag IDs (not slugs), matching the TUS prepare contract. */
  tags?: number[] | undefined;
  publish_mode?: 'none' | 'auto' | 'scheduled' | undefined;
  scheduled_at?: string | undefined;
}

/**
 * Response of `POST /studio/videos/fetch-from-url`. Flat shape — fields sit at
 * the top level alongside `message`, same as PrepareBunnyUploadResponse (no
 * ApiResponse envelope). Issued the moment Bunny accepts the fetch job, NOT
 * when the download finishes — progress arrives later via the same webhook
 * path as TUS uploads.
 */
export interface FetchFromUrlResponse {
  video_id: number;
  video_uuid: string;
  bunny_video_id: string;
  source_host: string | null;
  /** True when an idempotent client_id matched an existing row. */
  deduplicated: boolean;
}

/**
 * Content Management Types
 * Extended types for the Content Management Hub
 */

/**
 * Processing status for videos being uploaded/encoded
 */
export type ProcessingStatus = 'uploading' | 'processing' | 'ready' | 'failed';

/**
 * Unified payload for PATCH /api/studio/videos/{videoUuid}
 * Combines metadata and publication settings in a single request
 */
export interface UpdateVideoPayload {
  title?: string;
  description?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  country_id?: number;
  is_adult_content?: boolean;
  is_substance_content?: boolean;
  is_exclusive_content?: boolean;
  is_public?: boolean;
  featured?: boolean;
  hidden?: boolean;
  published_at?: string;
  /** Array of tag SLUGS (not IDs) */
  tags?: string[];
  /** Thumbnail file for FormData upload */
  thumbnail?: File;
  publish_mode?: PublicationMode;
  /** Required if publish_mode='scheduled' (ISO 8601 datetime) */
  scheduled_at?: string;
}

/**
 * Response for PATCH /api/studio/videos/{videoUuid}/toggle-hidden.
 * BO's `ResponseMethodsTrait::sendResponse` spreads the payload at the top
 * level — there is no `success` / `data` envelope (FRONT-352: the old
 * `{ data: { hidden } }` type crashed every toggle on `response.data.hidden`).
 */
export interface ToggleHiddenResponse {
  message: string;
  hidden: boolean;
}

/**
 * Response for DELETE /api/studio/videos/{videoUuid} — same flat envelope.
 */
export interface DeleteVideoResponse {
  message: string;
}

export interface UpdateVideoResponse {
  success: boolean;
  video?: StudioVideoListItem;
  errors?: Record<string, string[]>;
}

/** Payload for POST /api/studio/videos/{videoUuid}/schedule */
export interface CreateSchedulePayload {
  publish_mode: 'scheduled';
  scheduled_at?: string; // Required if publish_mode='scheduled'
  notify?: boolean;
}

/** Payload for PATCH /api/studio/videos/{videoUuid}/schedule */
export interface UpdateSchedulePayload {
  publish_mode?: 'scheduled';
  scheduled_at?: string;
  notify?: boolean;
}

/** Schedule object returned by API */
export interface VideoPublishSchedule {
  id: number;
  video_id: number;
  publish_mode: 'auto' | 'scheduled';
  scheduled_at: string | null;
  notify: boolean;
  created_at: string;
  updated_at: string;
}

/** Response for POST/PATCH schedule endpoints */
export interface ScheduleResponse {
  success: boolean;
  data: { schedule: VideoPublishSchedule };
  message: string;
}

/** Response for DELETE schedule endpoint */
export interface DeleteScheduleResponse {
  success: boolean;
  data: [];
  message: string;
}

/**
 * Content type enum for studio video listing
 */
export type StudioVideoContentType = 'videos' | 'series' | 'courses' | 'shorts';

/**
 * Sort options for studio video listing
 */
export type StudioVideoSortBy = 'latest' | 'oldest';

/**
 * Query parameters for GET /api/studio/videos
 */
export interface StudioVideosQueryParams {
  /** Page number. Default: 1 */
  page?: number;
  /** Items per page. Default: 20, Max: 100 */
  per_page?: number;
  /** Filter by specific video IDs */
  ids?: number[];
  /** Filter by country names */
  countries?: string[];
  /** Filter by country IDs */
  countries_ids?: number[];
  /** Filter by series IDs */
  series_ids?: number[];
  /** Filter by content types. Default: ['videos', 'series'] */
  types?: StudioVideoContentType[];
  /** Sort order. Default: 'latest' */
  sort_by?: StudioVideoSortBy;
  /** Filter by video status: published, draft, scheduled, processing */
  status?: 'published' | 'draft' | 'scheduled' | 'processing';
}
